import type { BrandStyle } from '@/types/design'

const CORE_TOKENS = [
  '--theme-ground',
  '--theme-surface',
  '--theme-ink',
  '--theme-accent',
  '--theme-text-on-accent',
] as const

/** The theme tokens every contract v2 template exposes. */
export type ThemeToken = (typeof CORE_TOKENS)[number] | '--theme-font-display' | '--theme-font-body'

export type Theme = Partial<Record<ThemeToken, string>>

/** A template's own colours. Mirrors the server's `TemplateTheme`. */
export type TemplateTheme = Record<(typeof CORE_TOKENS)[number], string>

/** Which colours the template grid shows: the brand's or each template's own. */
export type TemplateColourMode = 'brand' | 'template'

/** A pick of accent (`null` takes the first brand colour) and font on a template. */
export type ThemeChoice = Pick<BrandStyle, 'accent' | 'font'>

/** The 17 library templates, `t01` to `t17`. */
export const TEMPLATE_IDS = Array.from(
  { length: 17 },
  (_, index) => `t${String(index + 1).padStart(2, '0')}`,
)

/** The six layouts the template viewer shows, in order. */
export const SAMPLE_LAYOUTS = ['title', 'statement', 'stat', 'quote', 'takeaway', 'sign-off']

function channel(hex: string, offset: number): number {
  const value = parseInt(hex.slice(offset, offset + 2), 16) / 255
  return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
}

/** The WCAG relative luminance of a `#rrggbb` colour. */
export function luminance(hex: string): number {
  return 0.2126 * channel(hex, 1) + 0.7152 * channel(hex, 3) + 0.0722 * channel(hex, 5)
}

/** The brand colours on a template. Mirrors the server's `buildBrandTheme`. */
export function buildBrandTheme(colors: readonly string[], template: TemplateTheme): Theme {
  const [accent] = colors
  if (accent === undefined) return {}
  const byLuminance = [...colors].sort((a, b) => luminance(a) - luminance(b))
  const darkest = byLuminance[0] ?? accent
  const lightest = byLuminance[byLuminance.length - 1] ?? accent
  return {
    '--theme-accent': accent,
    ...(luminance(darkest) <= 0.05 && luminance(template['--theme-ink']) < 0.2
      ? { '--theme-ink': darkest }
      : {}),
    ...(luminance(lightest) >= 0.85 && luminance(template['--theme-ground']) > 0.6
      ? { '--theme-ground': lightest }
      : {}),
    '--theme-text-on-accent': luminance(accent) > 0.4 ? darkest : '#ffffff',
  }
}

/** The family of `fontFamily` that the templates ship, matched without case; `null` when none does. */
export function shippedFontOf(
  fontFamily: string | null,
  families: readonly string[],
): string | null {
  const wanted = fontFamily?.trim().toLowerCase()
  return families.find((family) => family.toLowerCase() === wanted) ?? null
}

/** The theme a brand style puts on a template. Mirrors the server's `brandThemeOf`; `brandFont` is the shipped brand font. */
export function brandThemeOf(
  colors: readonly string[],
  brandFont: string | null,
  { accent, font }: ThemeChoice,
  template: TemplateTheme,
): Theme {
  const ordered = accent === null ? colors : [accent, ...colors.filter((color) => color !== accent)]
  return {
    ...buildBrandTheme(ordered, template),
    ...(font === 'brand' && brandFont !== null
      ? { '--theme-font-display': brandFont, '--theme-font-body': brandFont }
      : {}),
  }
}

/** A template's own colours, read from its file; `null` when a token is missing. Mirrors the server's `templateThemeOf`. */
export function templateThemeOf(html: string): TemplateTheme | null {
  const entries = CORE_TOKENS.map((token) => [
    token,
    new RegExp(`${token}:\\s*(#[0-9a-fA-F]{6})\\b`).exec(html)?.[1]?.toLowerCase(),
  ])
  return entries.every(([, value]) => value !== undefined)
    ? (Object.fromEntries(entries) as TemplateTheme)
    : null
}

/** The first family of a template's display font, without quotes. */
export function displayFontOf(html: string): string {
  const stack = /--theme-font-display:\s*([^;]+);/.exec(html)?.[1] ?? ''
  return (stack.split(',')[0] ?? '').replace(/["']/g, '').trim()
}

/** True when a template's own ground is dark. */
export function isDarkTemplate(template: TemplateTheme): boolean {
  return luminance(template['--theme-ground']) < 0.3
}
